import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const SouthEastTexasCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Real-Time Feedback, Real Results | Curipod"
      subTitle="See how Curipod drove 32% growth in STAAR scores at a middle school in South Texas — from 46% to 84% Meets & Masters in 6th grade."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>From 46% to 84%</HeroTitle>
            <HeroSubtitle>
              Real-Time Feedback, Real Results: 32% Growth in STAAR Scores
            </HeroSubtitle>
            <HeroDescription>
              How one teacher&apos;s experiment with Curipod sparked a school-wide
              transformation — lifting STAAR writing scores across all three grade levels
              at a middle school in South Texas.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Middle School in South Texas</MetaBadge>
              <MetaBadge>88% FRPL · 99% Hispanic</MetaBadge>
              <MetaBadge>Grades 6–8 RLA</MetaBadge>
              <MetaBadge>2022–2025</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    46<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>6th Grade Meets &amp; Masters</StatLabel>
                  <StatYear>2022–2023 · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>→</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    84<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>6th Grade Meets &amp; Masters</StatLabel>
                  <StatYear>2024–2025 · With Curipod</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem>
                  <StatNumber>
                    57<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>7th Grade Meets &amp; Masters</StatLabel>
                  <StatYear>2023–2024 · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>→</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    78<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>7th Grade Meets &amp; Masters</StatLabel>
                  <StatYear>2024–2025 · With Curipod</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* YEAR ONE: BREAKTHROUGH IN 6TH GRADE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>YEAR ONE</SectionTag>
              <SectionTitle>Breakthrough in 6th Grade</SectionTitle>
              <SectionText>
                Ms. G has dedicated years to supporting her students. She built
                relationships with families. She stayed late to support struggling
                readers. She worked tirelessly to make learning engaging.
              </SectionText>
              <SectionText>
                Still, nearly half of her students weren&apos;t meeting grade-level
                expectations on the state&apos;s Reading Language Arts (RLA) assessment.
                She knew something had to change.
              </SectionText>
              <SectionText>
                Then, in the spring of 2023, Ms. G tried something new: she used Curipod
                for writing instruction and STAAR-aligned test prep. What started as an
                experiment quickly became a classroom transformation.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  I just needed something that would grab them — and it did. Every single
                  lesson blew their minds.
                </QuoteText>
                <QuoteAttrib>— Ms. G, 6th Grade Teacher</QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamPhotoArea>
                <ScreenshotImageWrap>
                  <Image
                    src="/case-study-south-east-texas/student-laptop.jpeg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Student using Curipod on laptop"
                  />
                </ScreenshotImageWrap>
                <TeamPhotoCaption>
                  A student engaging with Curipod in class
                </TeamPhotoCaption>
              </TeamPhotoArea>
              <TeamCardTitle>The Transformation</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>Lead Teacher</TeamRole>
                  <TeamName>Ms. G</TeamName>
                  <TeamNote>Pioneered Curipod in her 6th grade classroom</TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>School Demographics</TeamRole>
                  <TeamName>88% FRPL · 99% Hispanic</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Focus Areas</TeamRole>
                  <TeamName>
                    Whole-class engagement, writing instruction, STAAR performance
                  </TeamName>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* WHAT MADE THE DIFFERENCE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHAT MADE THE DIFFERENCE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Three Keys to Breakthrough
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Students who rarely engaged were suddenly competing for top-voted responses.
              They shared ideas, analyzed writing, and revised their work — in the same
              class period. With real-time feedback and engaging standards-aligned
              prompts, writing became less about pressure and more about progress.
            </SectionText>

            <ImpactGrid>
              <ImpactCard>
                <ImpactIcon>✍️</ImpactIcon>
                <ImpactTitle>Student Agency</ImpactTitle>
                <ImpactQuote>
                  Students wrote about what mattered to them — Taylor Swift, the World
                  Cup, their favorite food — while mastering state-aligned skills.
                </ImpactQuote>
              </ImpactCard>

              <ImpactCard>
                <ImpactIcon>💬</ImpactIcon>
                <ImpactTitle>Real-Time Feedback</ImpactTitle>
                <ImpactQuote>
                  Curipod gave personalized, immediate feedback on every response.
                  Students then analyzed their feedback and made thoughtful changes in
                  real time.
                </ImpactQuote>
              </ImpactCard>

              <ImpactCard $wide>
                <ImpactIcon>🤝</ImpactIcon>
                <ImpactTitle>Culture of Learning</ImpactTitle>
                <ImpactQuote>
                  Students read each other&apos;s work, debated scores, and worked to
                  improve. What was once a solitary activity became a collaborative
                  experience of shared growth.
                </ImpactQuote>
              </ImpactCard>
            </ImpactGrid>

            <BlockQuote style={{ maxWidth: 620, margin: '40px auto 0' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                I saw students eager and excited to learn! And actually wanting to write…
                I get goosebumps. Just look at the scores. That&apos;s the bottom line.
              </QuoteText>
              <QuoteAttrib>— Principal</QuoteAttrib>
            </BlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* CURIPOD IN ACTION */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              CURIPOD IN ACTION
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Inside the Classroom
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              From reading passages to word clouds and AI-powered feedback, Curipod turned
              writing practice into something students wanted to do.
            </SectionText>
            <ScreenshotGrid>
              <ScreenshotCard>
                <ScreenshotContainer>
                  <Image
                    src="/case-study-south-east-texas/curipod-read-passage.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod Read the Passage lesson about Old Faithful"
                  />
                </ScreenshotContainer>
                <ScreenshotCaption>
                  Standards-aligned reading passages spark engagement
                </ScreenshotCaption>
              </ScreenshotCard>

              <ScreenshotCard>
                <ScreenshotContainer>
                  <Image
                    src="/case-study-south-east-texas/curipod-word-cloud.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod word cloud showing student impressions"
                  />
                </ScreenshotContainer>
                <ScreenshotCaption>
                  Word clouds capture every student&apos;s voice
                </ScreenshotCaption>
              </ScreenshotCard>

              <ScreenshotCard>
                <ScreenshotContainer>
                  <Image
                    src="/case-study-south-east-texas/curipod-ai-feedback.png"
                    layout="fill"
                    objectFit="contain"
                    alt="Curipod AI feedback on student writing"
                  />
                </ScreenshotContainer>
                <ScreenshotCaption>
                  AI-powered feedback helps students revise in real time
                </ScreenshotCaption>
              </ScreenshotCard>
            </ScreenshotGrid>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* YEAR TWO: SCHOOL-WIDE MOVEMENT */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              YEAR TWO
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Classroom Experiment to School-Wide Movement
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Seeing the growth in 6th grade, the whole school started buzzing about
              Curipod. It wasn&apos;t just about adopting a new platform. It was about
              equipping teachers to bring the energy, feedback, and confidence-building
              strategies to their students that Ms. G achieved in her classroom.
            </SectionText>

            <BlockQuote style={{ maxWidth: 620, margin: '0 auto 40px' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                I told the team, &lsquo;Just try it once a week.&rsquo; When the kids lit
                up, teachers were all in.
              </QuoteText>
              <QuoteAttrib>— Ms. G, 6th Grade Teacher</QuoteAttrib>
            </BlockQuote>

            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 0' }}
            >
              With Curipod, STAAR prep wasn&apos;t high-stakes cramming. It was embedded
              throughout the year, blending accountability with joyful learning.
              Confidence rose. Engagement deepened. Teachers felt re-energized. And
              writing scores didn&apos;t just rise — they soared to a level previously
              considered unreachable.
            </SectionText>

            <FocusAreaGrid style={{ marginTop: 40 }}>
              <FocusAreaBadge>Whole-Class Engagement</FocusAreaBadge>
              <FocusAreaBadge>Writing Instruction</FocusAreaBadge>
              <FocusAreaBadge>STAAR Performance</FocusAreaBadge>
            </FocusAreaGrid>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              RESULTS THAT SPEAK FOR THEMSELVES
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              Scores That Soared
            </SectionTitle>

            <ResultsGrid>
              <ResultCard>
                <ResultGrade>6th Grade</ResultGrade>
                <ResultStats>
                  <ResultOld>46%</ResultOld>
                  <ResultArrow>→</ResultArrow>
                  <ResultNew>84%</ResultNew>
                </ResultStats>
                <ResultNote>Meets &amp; Masters (two years with Curipod)</ResultNote>
              </ResultCard>

              <ResultCard>
                <ResultGrade>7th Grade</ResultGrade>
                <ResultStats>
                  <ResultOld>57%</ResultOld>
                  <ResultArrow>→</ResultArrow>
                  <ResultNew>78%</ResultNew>
                </ResultStats>
                <ResultNote>Meets &amp; Masters (one year with Curipod)</ResultNote>
              </ResultCard>

              <ResultCard>
                <ResultGrade>8th Grade</ResultGrade>
                <ResultStats>
                  <ResultOld>42%</ResultOld>
                  <ResultArrow>→</ResultArrow>
                  <ResultNew>56%</ResultNew>
                </ResultStats>
                <ResultNote>Meets &amp; Masters (one year with Curipod)</ResultNote>
              </ResultCard>
            </ResultsGrid>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                Curipod helped us make writing less intimidating. It wasn&apos;t about
                getting it perfect. It was about getting better.
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>— Teacher</QuoteAttrib>
            </ResultBlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>Why It Matters</SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Curipod didn&apos;t just get students ready for the test. It got them
              excited to show up and show what they know. Great teachers work day after
              day to provide high-impact instruction that builds confidence, sparks
              engagement, and grows real skills. Curipod makes it easier for teachers to
              do what they do best — and see the impact of their efforts in student
              achievement.
            </SectionText>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                This isn&apos;t just about writing. It&apos;s about giving students a
                reason to believe in their own thinking.
              </FinalQuoteText>
              <QuoteAttrib>— Ms. G, 6th Grade Teacher</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {['Engagement', 'Writing', 'Feedback', 'Confidence', 'Equity'].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the South East Texas case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_South_East_Texas.pdf"
              download="Curipod_Case_Study_South_East_Texas.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default SouthEastTexasCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamPhotoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid ${theme.colors.beigeDark};
`;

const ScreenshotImageWrap = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid ${theme.colors.orange};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

const TeamPhotoCaption = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

/* ─── SCREENSHOTS ────────────────────────────────────────────────────────────── */

const ScreenshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ScreenshotCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 16px;
  box-shadow: ${theme.effects.defaultShadow};
  transition: ${theme.effects.defaultTransition};

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }
`;

const ScreenshotContainer = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
`;

const ScreenshotCaption = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  text-align: center;
  margin: 12px 0 0;
  font-weight: 500;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  margin-bottom: 48px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ResultCard = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
`;

const ResultGrade = styled.h3`
  font-size: 16px;
  color: ${theme.colors.orange};
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const ResultStats = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ResultOld = styled.span`
  font-size: 36px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.5);

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const ResultArrow = styled.span`
  font-size: 24px;
  color: rgba(255, 255, 255, 0.4);
`;

const ResultNew = styled.span`
  font-size: 44px;
  font-weight: bold;
  color: ${theme.colors.orange};

  @media ${MOBILE} {
    font-size: 36px;
  }
`;

const ResultNote = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
